import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import { useParams } from "react-router-dom";
import { useAppDispatch } from "../../../framework/customStore";
import { showApiError, showApiSuccess } from "../../framework/formUtils";
import { createInvoiceRow, updateInvoiceRow } from "../../../services/newInvoiceService";
import { fetchTransportOrderInvoice } from "../../../store/transportOrderInvoiceSlice";
import { useTranslation } from "react-i18next";
import { InvoiceRowAddDto } from "../../../models/transportOrderInvoice/invoiceRowAddDto";
import { InvoiceRowType } from "../../../models/invoice/invoiceRowType";
import { TransportOrderInvoiceRowDto } from "../../../models/transportOrderInvoice/TransportOrderInvoiceRowDto";
import { InvoiceRowUpdateDto } from "../../../models/transportOrderInvoice/invoiceRowUpdateDto";
import { InvoiceRowListItemDto } from "../../../models/transportOrderInvoice/invoiceRowListItemDto";

interface TransportOrderInvoiceNewRowDialogProps {
    open: boolean;
    transportOrderInvoice: TransportOrderInvoiceRowDto;
    invoiceRow?: InvoiceRowListItemDto;
    onClose: () => void;
}

export const TransportOrderInvoiceNewRowDialog = ({ open, onClose, transportOrderInvoice, invoiceRow }: TransportOrderInvoiceNewRowDialogProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [message, setMessage] = useState("");
    const newInvoiceRowData: InvoiceRowAddDto = {
        invoiceNewId: transportOrderInvoice.rows[0].invoiceNewId,
        description: message,
        rowType: InvoiceRowType.Text,
        transportOrderId: transportOrderInvoice.orderId
    };
    const { id } = useParams<{ id: string }>();

    useEffect(() => {
        if (invoiceRow) setMessage(invoiceRow?.description);
    }, [invoiceRow]);

    const handleAddSelected = () => {
        if (invoiceRow) {
            const data: InvoiceRowUpdateDto = {
                description: message,
            };

            updateInvoiceRow(invoiceRow.id, data)
                .then(() => {
                    showApiSuccess(t("transport.invoicing.invoiceRowUpdated"));
                    dispatch(fetchTransportOrderInvoice(id) as any);
                    onClose();
                })
                .catch(showApiError);
        } else {
            createInvoiceRow(newInvoiceRowData)
                .then(() => {
                    showApiSuccess(t("transport.invoicing.invoiceRowAdded"));
                    dispatch(fetchTransportOrderInvoice(id) as any);
                    onClose();
                })
                .catch(showApiError);
        }
    };

    const getTitile = () => {
        if (invoiceRow) return t("transport.invoicing.updateRowToOrderInvoice");
        return t("transport.invoicing.addRowToOrderInvoice");
    };

    const getButtonText = () => {
        if (invoiceRow) return t("transport.invoicing.updateRow");
        return t("transport.invoicing.addRow");
    };

    const handleMessageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setMessage(event.target.value);
    };

    return (
        <Dialog
            open={open}
            title={getTitile()}
            onClose={onClose}
            fullWidth
            maxWidth={"sm"}
        >
            <DialogTitle>
                <Grid2 container justifyContent="space-between" alignItems="center">
                    <Typography variant="h2">{getTitile()}</Typography>
                    <Button color="primary" endIcon={<CloseIcon />} onClick={onClose} />
                </Grid2>
                <Grid2>
                    <Typography variant="subtitle1">
                        {transportOrderInvoice.name} ({t("common.order")}: {transportOrderInvoice.orderNumber})
                    </Typography>
                </Grid2>
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: "200px" }}>
                <TextField
                    id="row-description"
                    multiline
                    rows={6}
                    fullWidth
                    value={message}
                    onChange={handleMessageChange}
                />
            </DialogContent>
            <DialogActions className="mx-3 my-2">
                <Button
                    color="success"
                    variant={"contained"}
                    onClick={handleAddSelected}
                    disabled={message.length <= 0}
                >
                    {getButtonText()}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
